import React, { useCallback, useState } from "react";
import { Plus } from "lucide-react";

import { SideNavigationMenu } from "../../components/library/side-navigation-menu/SideNavigationMenu";
import { GridCanvas, type CanvasDropArgs } from "../../components/grid-canvas/GridCanvas";
import { WidgetPicker } from "../../components/widget-picker/WidgetPicker";
import {
  MarketMonitorDashboardProvider,
  useMarketMonitorDashboard,
} from "../../features/market-monitor/dashboards/MarketMonitorDashboardProvider";
import { DashboardSelector } from "../../features/market-monitor/dashboards/DashboardSelector";
import { marketMonitorWidgetRegistry } from "../../features/market-monitor/widgets";
import {
  MONITOR_DSL_DRAG_MIME,
  type MonitorDslDragPayload,
} from "../../features/market-monitor/dashboard/ChartLegend";
import { SendEmailButton } from "../../features/market-monitor/email/SendEmailButton";
import { ScheduleEmailButton } from "../../features/market-monitor/email/ScheduleEmailButton";
import { RemoveScheduleButton } from "../../features/market-monitor/email/RemoveScheduleButton";
import { MarketMonitorLiveProvider, useMarketMonitorLiveContext } from "../../features/market-monitor/live/MarketMonitorLiveContext";
import { TickCadenceSelect } from "../../features/market-monitor/live/TickCadenceSelect";
import styles from "./MarketMonitorPage.module.css";

const CANVAS_ACCEPTED_DROP_MIMES = [MONITOR_DSL_DRAG_MIME];

const MarketMonitorContent: React.FC = () => {
  const state = useMarketMonitorDashboard();
  const live = useMarketMonitorLiveContext();
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleCanvasDrop = useCallback(
    (args: CanvasDropArgs) => {
      if (args.mime !== MONITOR_DSL_DRAG_MIME) return;
      let payload: MonitorDslDragPayload;
      try {
        payload = JSON.parse(args.payload) as MonitorDslDragPayload;
      } catch {
        return;
      }
      const seedDsl = payload?.dsl?.trim();
      if (!seedDsl) return;
      state.addChartWidgetAt({
        x: args.gridX,
        y: args.gridY,
        seedDsls: [seedDsl],
      });
    },
    [state],
  );

  const dashboardEmptyState = (
    <div className={styles.canvasEmpty}>
      <span className={styles.canvasEmptyTitle}>No widgets yet</span>
      <span className={styles.canvasEmptyDescription}>
        Click <strong>+ Add widget</strong> to add your first chart, or
        drag a tag onto the canvas.
      </span>
    </div>
  );

  return (
    <div className={styles.page}>
      <SideNavigationMenu isOpen={true} isCollapsed={true} lockCollapsed={true} />

      <header className={styles.topHeader}>
        <h1 className={styles.headerTitle}>Market Monitor</h1>
      </header>

      <main className={styles.content}>
        <div className={styles.dashboardHeader}>
          <DashboardSelector state={state} />
          <div className={styles.headerActions}>
            {live.enabled && (
              <TickCadenceSelect
                value={live.cadence}
                onChange={live.setCadence}
                status={live.status}
              />
            )}
            <button
              type="button"
              className={styles.addWidgetButton}
              onClick={() => setPickerOpen(true)}
              data-testid="market-monitor-add-widget"
              aria-label="Add widget"
            >
              <Plus size={14} aria-hidden="true" />
              <span>Add widget</span>
            </button>
            <SendEmailButton state={state} />
            <ScheduleEmailButton state={state} />
            <RemoveScheduleButton state={state} />
          </div>
        </div>

        <GridCanvas
          widgets={state.widgets}
          registry={marketMonitorWidgetRegistry}
          onLayoutChange={state.updateLayout}
          onRemoveWidget={state.removeWidget}
          emptyState={dashboardEmptyState}
          acceptedDropMimes={CANVAS_ACCEPTED_DROP_MIMES}
          onCanvasDrop={handleCanvasDrop}
        />
      </main>

      <WidgetPicker
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        registry={marketMonitorWidgetRegistry}
        title="Add Widget"
        onAddWidget={(widgetType) => {
          state.addWidget(widgetType);
          setPickerOpen(false);
        }}
      />
    </div>
  );
};

const MarketMonitorPage: React.FC = () => (
  <MarketMonitorLiveProvider>
    <MarketMonitorDashboardProvider>
      <MarketMonitorContent />
    </MarketMonitorDashboardProvider>
  </MarketMonitorLiveProvider>
);

export default MarketMonitorPage;
